import { useState } from 'react';
import { Cpu, Brain, ChevronRight, Zap } from 'lucide-react';

function ProbBar({ value, color }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className="w-12 text-right font-bold tabular-nums text-sm" style={{ color }}>
        {(value * 100).toFixed(0)}%
      </span>
    </div>
  );
}

function probColor(p) {
  if (p >= 0.8) return '#ef4444';
  if (p >= 0.5) return '#f59e0b';
  if (p >= 0.2) return '#6366f1';
  return '#94a3b8';
}

function Spinner() {
  return (
    <svg className="animate-spin-slow h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

const STEPS = [
  'Loading variant context...',
  'Embedding genomic features...',
  'Cross-referencing HPO ontology...',
  'Scoring disease associations...',
  'Finalizing predictions...',
];

export default function PhenomeAI({ data }) {
  const [status, setStatus] = useState('idle');
  const [stepIdx, setStepIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  const runAnalysis = () => {
    setStatus('running');
    setProgress(0);
    setStepIdx(0);

    const progressInterval = setInterval(() => {
      setProgress((p) => {
        if (p >= 95) { clearInterval(progressInterval); return p; }
        return p + 3;
      });
    }, 60);

    const stepInterval = setInterval(() => {
      setStepIdx((s) => {
        if (s >= STEPS.length - 1) { clearInterval(stepInterval); return s; }
        return s + 1;
      });
    }, 380);

    setTimeout(() => {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
      setProgress(100);
      setTimeout(() => setStatus('results'), 300);
    }, 2000);
  };

  return (
    <section id="phenomeai" className="bg-white rounded-2xl shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{ background: '#021f30' }}>
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-white/70" />
          <h3 className="text-white font-bold text-base">PhenomeAI</h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
            style={{ background: 'rgba(249,52,65,0.3)' }}>
            AI-Powered
          </span>
        </div>
      </div>

      <div className="p-6">
        {status === 'idle' && (
          <div className="flex flex-col items-center gap-5 py-6 text-center animate-fade-in">
            <div className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(249,52,65,0.08)', border: '2px dashed #f9344150' }}>
              <Brain size={28} style={{ color: '#f93441' }} />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 text-lg">Disease & Phenotype Analysis</h4>
              <p className="text-gray-500 text-sm mt-1.5 max-w-sm leading-relaxed">
                PhenomeAI uses large language model embeddings and HPO ontology matching
                to predict disease associations and phenotypic consequences of this variant.
              </p>
            </div>
            <button
              onClick={runAnalysis}
              className="flex items-center gap-2 px-8 py-3.5 rounded-xl text-white font-bold text-base transition-all hover:brightness-110 active:scale-95 shadow-lg"
              style={{ background: '#f93441', boxShadow: '0 4px 16px rgba(249,52,65,0.3)' }}
            >
              <Zap size={18} />
              Run PhenomeAI
            </button>
            <p className="text-xs text-gray-400">~2 seconds · No PHI transmitted</p>
          </div>
        )}

        {status === 'running' && (
          <div className="flex flex-col items-center gap-6 py-6 animate-fade-in">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 rounded-full border-4 border-gray-100" />
              <svg className="absolute inset-0 animate-spin-slow" width="80" height="80" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="36" fill="none" stroke="#f93441" strokeWidth="4"
                  strokeDasharray={`${progress * 2.26} 226`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-bold text-gray-900">{progress}%</span>
              </div>
            </div>

            <div className="text-center">
              <h4 className="font-bold text-gray-900">Analyzing...</h4>
              <p className="text-sm text-gray-500 mt-1">{STEPS[stepIdx]}</p>
            </div>

            <div className="w-full max-w-sm">
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-100"
                  style={{ width: `${progress}%`, background: 'linear-gradient(to right, #f93441, #ff6b75)' }} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 w-full max-w-sm">
              {['HPO', 'OMIM', 'Orphanet'].map((db) => (
                <div key={db} className="text-center text-xs text-gray-400 py-2 rounded-lg border border-gray-100 animate-shimmer">
                  {db}
                </div>
              ))}
            </div>
          </div>
        )}

        {status === 'results' && (
          <div className="flex flex-col gap-6 animate-fade-in">
            <div className="flex items-center gap-2 text-green-600 text-sm font-semibold">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              Analysis complete
            </div>

            {/* Disease predictions */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Cpu size={14} className="text-gray-400" />
                Disease Probability Predictions
              </h4>
              <div className="flex flex-col gap-3">
                {data.diseases.map((d) => {
                  const c = probColor(d.probability);
                  return (
                    <div key={d.hpo} className="p-4 rounded-xl border border-gray-200 flex flex-col gap-2.5">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{d.name}</div>
                          <div className="font-mono text-xs text-gray-400 mt-0.5">{d.hpo}</div>
                        </div>
                        <div className="px-3 py-1 rounded-full text-xs font-bold text-white"
                          style={{ background: c }}>
                          {(d.probability * 100).toFixed(0)}%
                        </div>
                      </div>
                      <ProbBar value={d.probability} color={c} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* HPO phenotypes */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <ChevronRight size={14} className="text-gray-400" />
                Matched HPO Phenotypes
              </h4>
              <div className="grid sm:grid-cols-3 gap-3">
                {data.phenotypes.map((ph) => {
                  const c = probColor(ph.probability);
                  return (
                    <div key={ph.hpo} className="p-3 rounded-xl border border-gray-200 flex flex-col gap-1.5">
                      <div className="text-sm font-medium text-gray-800">{ph.name}</div>
                      <div className="font-mono text-xs text-gray-400">{ph.hpo}</div>
                      <ProbBar value={ph.probability} color={c} />
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              onClick={() => { setStatus('idle'); setProgress(0); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all w-fit"
            >
              Reset Analysis
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
